/**
 * sld-edge-cases.js
 *
 * Registry of SLD styling edge cases that sld-style.js consults while
 * building rule summaries and deriving the hoisted layer opacity.
 *
 * Each handler describes one anomaly that would otherwise render badly
 * (invisible swatches, zero-opacity layers, etc.) and how the loader
 * should adapt:
 *   - detect(rule)                → boolean
 *   - buildSummaryOverride(rule)  → partial summary to merge over the
 *                                   default rule summary
 *   - preserveFillOpacity         → if true, _stripOpacity in
 *                                   sld-style.js leaves the rule's
 *                                   fill-opacity intact (so a deliberate
 *                                   transparent fill survives the
 *                                   strip-and-hoist pass)
 *   - deriveHoistedOpacity(rule)  → optional override for the layer's
 *                                   hoisted opacity. The first rule
 *                                   that matches and supplies a hint
 *                                   wins (rules are walked in order).
 *
 * Edge cases registered today:
 *   1. transparent-fill-visible-stroke — polygon symbolizer with
 *      fillOpacity=0 and strokeOpacity>0. Used in catalog SLDs where
 *      the fill must stay transparent so OL's click-to-identify hit
 *      test still triggers inside the polygon, but the user still
 *      wants the boundary visible.
 *
 * Exposes evaluateRule(rule) → { summary, preserveFillOpacity,
 *                                hoistedOpacityHint, name } | null.
 */

function _firstSymbolizer(rule) {
  return (rule && rule.symbolizers && rule.symbolizers[0]) || null;
}

function _fillStyling(symbolizer) {
  return (symbolizer && symbolizer.fill && symbolizer.fill.styling) || null;
}

function _strokeStyling(symbolizer) {
  return (symbolizer && symbolizer.stroke && symbolizer.stroke.styling) || null;
}

// Polygon symbolizer with a deliberately transparent fill (alpha 0) but
// a visible stroke. The fill must stay alpha-0 so the polygon remains
// hit-testable for click-to-identify, but the legend swatch would
// otherwise render as an invisible rectangle and the hoisted layer
// opacity (currently taken from the first fill-opacity) would zero out
// the whole layer including the stroke.
const TRANSPARENT_FILL_VISIBLE_STROKE = {
  name: 'transparent-fill-visible-stroke',

  detect: function (rule) {
    const sym = _firstSymbolizer(rule);
    if (!sym) return false;
    const fillSt = _fillStyling(sym);
    const strokeSt = _strokeStyling(sym);
    if (!fillSt || !strokeSt) return false;
    const fillOp = fillSt.fillOpacity;
    const strokeOp = strokeSt.strokeOpacity;
    // Both attributes must be explicitly present — a missing fill-opacity
    // means "use the SLD default of 1.0", which is the common case and
    // doesn't need special handling.
    if (fillOp == null || strokeOp == null) return false;
    return Number(fillOp) === 0 && Number(strokeOp) > 0;
  },

  buildSummaryOverride: function (rule) {
    const sym = _firstSymbolizer(rule);
    const strokeSt = _strokeStyling(sym);
    return {
      // Switch the legend to the line-swatch shape so the user sees
      // the boundary instead of an invisible rectangle.
      symbolType: 'linesymbolizer',
      fill: null,
      fillOpacity: 0,
      stroke: strokeSt.stroke,
      strokeWidth: strokeSt.strokeWidth,
      // Carries the stroke opacity through to MapKey's line-swatch
      // renderer; MapKey applies it as inline CSS opacity.
      swatchOpacity: Number(strokeSt.strokeOpacity)
    };
  },

  // If we let _stripOpacity remove the fill-opacity, OL would render
  // the polygon fill at the default 1.0 and the layer opacity slider
  // would multiply against 1.0 — the fill would no longer be
  // transparent and click-to-identify would still work but the user
  // would lose the see-through cartography they explicitly authored.
  preserveFillOpacity: true,

  deriveHoistedOpacity: function (rule) {
    const sym = _firstSymbolizer(rule);
    return Number(_strokeStyling(sym).strokeOpacity);
  }
};

const HANDLERS = [
  TRANSPARENT_FILL_VISIBLE_STROKE
];

export function evaluateRule(rule) {
  for (let i = 0; i < HANDLERS.length; i++) {
    const h = HANDLERS[i];
    if (h.detect(rule)) {
      return {
        name: h.name,
        summary: h.buildSummaryOverride(rule),
        preserveFillOpacity: !!h.preserveFillOpacity,
        hoistedOpacityHint: h.deriveHoistedOpacity
          ? h.deriveHoistedOpacity(rule)
          : null
      };
    }
  }
  return null;
}
